import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import {
  PbECommerceSettingsUseCaseEnum,
  ReturnsTrackingInfoResponseLogisticsInfoReturnMethodEnum,
  ReturnsTrackingInfoResponseStatusEnum
} from '@itsrever/rever-api'
import { useTrackingInfo } from '@/hooks/use-tracking-info'
import { useAppSelector } from '@/redux/hooks'
import { NewItems } from '@/app/tracking/new-items'
import { ReturnedItems } from '@/app/tracking/returned-items'
import { ReturnLogistics } from '@/app/tracking/return-logistics'
import { LabelGeneratingBanner } from '@/app/tracking/label-generating-banner'
import { shouldPollLabel, useLabelURL } from '@/hooks/use-label-url'
import { RefundSummary } from '@/app/tracking/refund-summary'
import { ReturnRate } from '@/app/tracking/return-rate'
import { ReturnProgress } from '@/app/tracking/return-progress'
import { UploadPhotos } from '@/app/tracking/upload-photo/upload-photos'
import { PortalFooter } from '@/ui/layout/portal-footer'
import { LoadingOverlay } from '@/ui/components/loading-overlay'
import { Error } from '@/pages'

export const TrackingPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const posthog = usePostHog()

  const email = searchParams.get('email') ?? undefined
  const process_id = searchParams.get('processId') ?? undefined
  const source = searchParams.get('source') ?? 'direct'

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const isReverLite = settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

  const { data, status } = useTrackingInfo({
    email,
    process_id
  })

  // Before collection the label is the shopper's next step, so it leads the
  // single-column layout; afterwards it is reference material below the timeline.
  const isParcelCollected =
    data?.status !== ReturnsTrackingInfoResponseStatusEnum.Started &&
    data?.status !== ReturnsTrackingInfoResponseStatusEnum.DocumentUploadPending
  const labelStatus = data?.logistics_info?.label_status
  const pollLabel = shouldPollLabel(labelStatus, isParcelCollected)

  // Shares the logistics card's query; the page only needs it for the banner.
  const {
    data: labelURL,
    isLoading: isLoadingLabel,
    isError: isLabelError
  } = useLabelURL(
    { email, process_id },
    { enabled: pollLabel, poll: pollLabel }
  )

  useEffect(() => {
    if (status !== 'success') return
    posthog?.identify(
      `${email}-${settings.ecommerce_id}-${data?.customer_printed_order_id}`,
      {
        email,
        ecommerceId: settings.ecommerce_id,
        orderNumber: data?.customer_printed_order_id
      }
    )

    posthog?.capture('Tracking visit (main)', {
      source,
      status: data?.status,
      return_method: data?.logistics_info?.return_method,
      ecommerce_id: settings.ecommerce_id
    })
  }, [status, data, source, email, settings.ecommerce_id, posthog])

  const returnedItems = data?.summary?.returned_items ?? []

  const exchangeOrder = data?.exchange_order_info
  const exchangeItems = data?.summary?.exchange_items

  const refundSummary = data?.summary
  const refundInfo = data?.refund_info

  const npsParams = new URLSearchParams({
    returnId: process_id ?? '',
    ecommerceId: settings.ecommerce_id ?? '',
    source: 'trackinginterface'
  })

  const npsUrl = `https://form.typeform.com/to/k7Jhs21E#${npsParams.toString()}`

  if (status === 'pending') return <LoadingOverlay loading={true} />

  if (status === 'error') return <Error />

  const showReturnLogistics =
    data.status !== 'AUTHORIZATION_PENDING' &&
    !!data?.logistics_info &&
    data.logistics_info.return_method !==
      ReturnsTrackingInfoResponseLogisticsInfoReturnMethodEnum.NoReturnMethod

  const showLabelGenerating =
    showReturnLogistics &&
    pollLabel &&
    !isLoadingLabel &&
    !isLabelError &&
    !labelURL

  const isTerminalStatus =
    data.status === ReturnsTrackingInfoResponseStatusEnum.Canceled ||
    data.status === ReturnsTrackingInfoResponseStatusEnum.NotAuthorized

  return (
    <div className="flex min-h-dvh flex-col items-center bg-[#f5f5f5]/60">
      <div className="flex w-full items-center justify-center border-b border-solid border-neutral-200 bg-white p-6">
        {settings.branding?.logo_url && (
          <div className="h-7">
            <img
              src={settings.branding?.logo_url}
              alt={settings.branding?.title}
              className="h-full w-auto object-contain"
            />
          </div>
        )}
      </div>
      <div className="flex w-full flex-col items-center space-y-7 px-4 pb-4 pt-7">
        <main
          className="w-full"
          style={{ maxWidth: showReturnLogistics ? '60rem' : '42rem' }}
        >
          {!isTerminalStatus && showLabelGenerating ? (
            <div className="mb-4">
              <LabelGeneratingBanner />
            </div>
          ) : null}
          {!isTerminalStatus &&
          data.upload_documents_info?.upload_documents_enabled ? (
            <div className="mb-4">
              <UploadPhotos uploadDocumentsInfo={data.upload_documents_info} />
            </div>
          ) : null}
          <div
            className={`grid w-full grid-cols-1 gap-4 lg:items-start ${
              showReturnLogistics ? 'lg:grid-cols-[minmax(0,1fr)_20rem]' : ''
            }`}
          >
            <div className="h-full space-y-4 lg:col-start-1 lg:row-start-1">
              <section
                aria-labelledby="return-progress-title"
                className="h-full w-full rounded-lg border border-neutral-300/50 bg-white px-6 pb-7 pt-6 shadow-sm"
              >
                <p className="pb-0.5 text-xs font-normal text-neutral-600">
                  {t('tracking_new.order', {
                    orderId: data.customer_printed_order_id
                  })}
                </p>
                <ReturnProgress
                  status={data?.status}
                  refundInfo={refundInfo}
                  logisticsInfo={data?.logistics_info}
                />
              </section>
            </div>
            {showReturnLogistics && data?.logistics_info ? (
              <aside
                className={`h-fit w-full lg:sticky lg:top-4 lg:order-none lg:col-start-2 lg:row-start-1 lg:max-w-80 ${
                  isParcelCollected ? '' : 'order-first'
                }`}
              >
                <ReturnLogistics
                  logisticsInfo={data?.logistics_info}
                  isParcelCollected={isParcelCollected}
                />
              </aside>
            ) : null}
            <div className="flex flex-col gap-4 lg:col-start-1 lg:row-start-2">
              <ReturnedItems returnedItems={returnedItems} />
              {exchangeItems ? (
                <NewItems
                  exchangeItems={exchangeItems}
                  exchangeOrder={exchangeOrder?.order_number}
                />
              ) : null}
              {refundSummary && refundInfo ? (
                <RefundSummary
                  summary={refundSummary}
                  refundInfo={refundInfo}
                />
              ) : null}
              {!isReverLite &&
              (refundInfo?.status === 'FULLY_REFUNDED' ||
                refundInfo?.status === 'REFUND_NOT_NECESSARY') ? (
                <ReturnRate href={npsUrl} />
              ) : null}
            </div>
          </div>
        </main>
        <PortalFooter />
      </div>
    </div>
  )
}
